import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { ref, onValue, off, update, push } from "firebase/database";
import { getStorage, ref as storageRef, getDownloadURL, uploadBytes } from "firebase/storage";
import { db, app } from "../../configs/Firebase";
import TitleBar from "../../designSystem/c-organism/titleBar/TitleBar";
import Button from "../../designSystem/b-molecules/buttons/Button";
import Icon from "../../designSystem/a-atoms/icons/Icon";
import Input from "../../designSystem/b-molecules/formElements/input/Input";
import Select from "../../designSystem/b-molecules/formElements/select/Select";
import Label from "../../designSystem/c-organism/titleBar/Label";
import Dropdown from "../../designSystem/c-organism/dialogs/Dropdown";
import PersonalitySlider from "./components/PersonalitySlider";
import MotivationBar from "./components/MotivationBar";
import ChannelBar from "./components/ChannelBar";
import style from "./PersonaDetail.module.css";
import { canEditSection, SECTIONS } from "../../utils/permissions";

const PersonaDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const org = useSelector((state) => state.orgStatus.value);
  const userStatus = useSelector((state) => state.userStatus.value);
  const [persona, setPersona] = useState(null);
  const [photoURL, setPhotoURL] = useState(null);
  const [user, setUser] = useState(null);
  const [labels, setLabels] = useState([]);
  const [brands, setBrands] = useState([]);
  const [canEdit, setCanEdit] = useState(false);
  const [showPersonaOptions, setShowPersonaOptions] = useState(false);
  const [personaOptionsStyle, setPersonaOptionsStyle] = useState();
  // Check if this is a new persona by looking at the pathname or id param
  const isNewPersona = id === "new" || location.pathname.endsWith("/new");
  const [isEditing, setIsEditing] = useState(isNewPersona);
  const [formData, setFormData] = useState(() => {
    // Initialize formData immediately for new personas
    // Check both id param and pathname since route structure may vary
    const pathname = window.location.pathname;
    const isNew = id === "new" || pathname.endsWith("/new");
    if (isNew) {
      const initialData = {
        name: "",
        tagline: "",
        quotation: "",
        demographics: {
          age: "",
          work: "",
          family: "",
          location: "",
          character: ""
        },
        personality: {
          introvertExtrovert: 50,
          thinkingFeeling: 50,
          sensingIntuition: 50,
          judgingPerceiving: 50
        },
        labelIds: [],
        about: "",
        goals: [],
        goalsLabelId: "",
        frustrations: [],
        motivation: [],
        brandIds: [],
        preferredChannels: []
      };
      return initialData;
    }
    return null;
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const photoInputRef = useRef(null);
  const storage = getStorage(app);

  // Get current user
  useEffect(() => {
    if (userStatus) {
      setUser(JSON.parse(userStatus.payload));
    }
  }, [userStatus]);

  // Check edit permissions
  useEffect(() => {
    if (user && org?.payload) {
      setCanEdit(canEditSection(user, org.payload, SECTIONS.PERSONAS));
    } else {
      setCanEdit(false);
    }
  }, [user, org?.payload]);

  // Load labels
  useEffect(() => {
    if (org?.payload) {
      const labelsRef = ref(db, `userData/${org.payload}/labels`);

      const unsubscribe = onValue(labelsRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const labelsList = Object.keys(data).map(id => ({
            id,
            ...data[id]
          }));
          setLabels(labelsList);
        } else {
          setLabels([]);
        }
      });

      return () => {
        off(labelsRef);
      };
    }
  }, [org?.payload]);

  // Load brands
  useEffect(() => {
    if (org?.payload) {
      const brandsRef = ref(db, `userData/${org.payload}/brands`);
      
      const unsubscribe = onValue(brandsRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const brandsList = Object.keys(data).map(id => ({
            id,
            ...data[id]
          }));
          setBrands(brandsList);
        } else {
          setBrands([]);
        }
      });

      return () => {
        off(brandsRef);
      };
    }
  }, [org?.payload]);


  // Load persona data
  useEffect(() => {
    if (org?.payload && id && !isNewPersona) {
      const personaRef = ref(db, `userData/${org.payload}/personas/${id}`);

      const unsubscribe = onValue(personaRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          setPersona({ id, ...data });

          // Load photo
          if (data.photo) {
            const photoPath = `${org.payload}/personas/${id}/${data.photo}`;
            const photoRef = storageRef(storage, photoPath);
            getDownloadURL(photoRef)
              .then((url) => setPhotoURL(url))
              .catch(() => setPhotoURL(null));
          } else {
            setPhotoURL(null);
          }
        } else {
          setPersona(null);
        }
      });

      return () => {
        off(personaRef);
      };
    } else if (isNewPersona) {
      setPersona(null);
      setPhotoURL(null);
    }
  }, [org?.payload, id, storage, isNewPersona]);

  // Reset formData and related state when navigating to "new"
  // This ensures proper initialization even if the component instance is reused
  useEffect(() => {
    if (isNewPersona) {
      const initialFormData = {
        name: "",
        tagline: "",
        quotation: "",
        demographics: {
          age: "",
          work: "",
          family: "",
          location: "",
          character: ""
        },
        personality: {
          introvertExtrovert: 50,
          thinkingFeeling: 50,
          sensingIntuition: 50,
          judgingPerceiving: 50
        },
        labelIds: [],
        about: "",
        goals: [],
        goalsLabelId: "",
        frustrations: [],
        motivation: [],
        brandIds: [],
        preferredChannels: []
      };
      setFormData(initialFormData);
      setIsEditing(true);
      setPersona(null);
      setPhotoURL(null);
      setPhotoFile(null);
      setPhotoPreview(null);
      setError("");
      if (photoInputRef.current) {
        photoInputRef.current.value = "";
      }
    }
  }, [id, isNewPersona, location.pathname]);

  const showPersonaOptionsHandler = (e) => {
    setShowPersonaOptions(true);
    setPersonaOptionsStyle({
      left: "auto",
      right: window.innerWidth - e.currentTarget.getBoundingClientRect().right,
      top: e.currentTarget.getBoundingClientRect().top,
    });
  };

  const closePersonaOptionsHandler = () => {
    setShowPersonaOptions(false);
  };

  const handleEdit = () => {
    if (!persona) return;
    
    // Initialize form data from persona
    const initialFormData = {
      name: persona.name || "",
      tagline: persona.tagline || "",
      quotation: persona.quotation || "",
      demographics: {
        age: persona.demographics?.age || "",
        work: persona.demographics?.work || persona.demographics?.role || "",
        family: persona.demographics?.family || "",
        location: persona.demographics?.location || "",
        character: persona.demographics?.character || persona.demographics?.segment || ""
      },
      personality: persona.personality || {
        introvertExtrovert: 50,
        thinkingFeeling: 50,
        sensingIntuition: 50,
        judgingPerceiving: 50
      },
      labelIds: persona.labelIds || [],
      about: persona.about || "",
      goals: persona.goals || [],
      goalsLabelId: persona.goalsLabelId || "",
      frustrations: persona.frustrations || [],
      motivation: (() => {
        if (persona.motivation) {
          if (Array.isArray(persona.motivation)) {
            return persona.motivation;
          } else {
            return Object.entries(persona.motivation).map(([key, value]) => {
              const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim();
              return { label, value };
            });
          }
        }
        return [];
      })(),
      brandIds: persona.brandIds || [],
      preferredChannels: (() => {
        if (persona.preferredChannels) {
          if (Array.isArray(persona.preferredChannels)) {
            return persona.preferredChannels;
          } else {
            return Object.entries(persona.preferredChannels).map(([key, value]) => {
              let label = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim();
              if (label === "Online Social Media") label = "Online & Social Media";
              if (label === "Guerrilla P R") label = "Guerrilla Efforts & PR";
              return { label, value };
            });
          }
        }
        return [];
      })()
    };
    
    setFormData(initialFormData);
    setPhotoPreview(photoURL);
    setPhotoFile(null);
    setIsEditing(true);
    setShowPersonaOptions(false);
    setError("");
  };

  const handleCancel = () => {
    if (isNewPersona) {
      navigate("/personas");
    } else {
      setIsEditing(false);
      setFormData(null);
      setPhotoFile(null);
      setPhotoPreview(null);
      setError("");
      if (photoInputRef.current) {
        photoInputRef.current.value = "";
      }
    }
  };

  const handlePhotoChange = (file) => {
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handlePhotoChange(e.dataTransfer.files[0]);
    }
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handlePhotoChange(e.target.files[0]);
    }
  };

  const handlePhotoButtonClick = (e) => {
    e.preventDefault();
    photoInputRef.current?.click();
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const parts = field.split('.');
      if (parts.length === 2) {
        const [parent, child] = parts;
        setFormData(prev => ({
          ...prev,
          [parent]: {
            ...(prev[parent] || {}),
            [child]: value
          }
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleSave = async () => {
    if (!formData || !org?.payload) return;

    // Validate required fields
    if (!formData.name.trim()) {
      setError("Persona name is required");
      return;
    }
    if (!formData.tagline.trim()) {
      setError("Tagline is required");
      return;
    }

    setSaving(true);
    setError("");

    try {
      if (isNewPersona) {
        // Create new persona
        const personaData = {
          ...formData,
          createdAt: { ".sv": "timestamp" },
          createdBy: user?.uid || null,
          updatedAt: { ".sv": "timestamp" }
        };

        const personasRef = ref(db, `userData/${org.payload}/personas`);
        const snapshot = await push(personasRef, personaData);
        const newPersonaId = snapshot.key;

        // Upload photo if selected
        if (photoFile && org.payload) {
          const extension = photoFile.type.replace(/(.*)\//g, "");
          const uuid = crypto.randomUUID();
          const filename = uuid + "." + extension;
          const finalPhotoRef = storageRef(storage, `${org.payload}/personas/${newPersonaId}/${filename}`);
          await uploadBytes(finalPhotoRef, photoFile);
          // Update photo filename in database
          const personaRef = ref(db, `userData/${org.payload}/personas/${newPersonaId}`);
          await update(personaRef, { photo: filename });
        }

        // Navigate to the new persona's page
        navigate(`/personas/${newPersonaId}`);
      } else {
        // Update existing persona
        const personaData = {
          ...formData,
          updatedAt: { ".sv": "timestamp" }
        };

        const personaRef = ref(db, `userData/${org.payload}/personas/${persona.id}`);
        await update(personaRef, personaData);

        // Upload photo if new file selected
        if (photoFile && org.payload) {
          const extension = photoFile.type.replace(/(.*)\//g, "");
          const uuid = crypto.randomUUID();
          const filename = uuid + "." + extension;
          const finalPhotoRef = storageRef(storage, `${org.payload}/personas/${persona.id}/${filename}`);
          await uploadBytes(finalPhotoRef, photoFile);
          // Update photo filename in database
          await update(personaRef, { photo: filename });
        }

        setIsEditing(false);
        setFormData(null);
        setPhotoFile(null);
        setPhotoPreview(null);
        if (photoInputRef.current) {
          photoInputRef.current.value = "";
        }
      }
    } catch (err) {
      console.error("Error saving persona:", err);
      setError("Failed to save persona: " + err.message);
      setSaving(false);
    }
  };

  const handleDuplicate = async () => {
    setShowPersonaOptions(false);
    if (!org?.payload || !persona || !window.confirm(`Duplicate "${persona.name}"?`)) {
      return;
    }

    try {
      const newPersona = {
        ...persona,
        name: `${persona.name} (Copy)`,
        createdAt: { ".sv": "timestamp" },
        updatedAt: { ".sv": "timestamp" },
        createdBy: user?.uid || null
      };
      delete newPersona.id;
      delete newPersona.photo;

      const personasRef = ref(db, `userData/${org.payload}/personas`);
      const snapshot = await push(personasRef, newPersona);
      navigate(`/personas/${snapshot.key}`);
    } catch (error) {
      console.error("Error duplicating persona:", error);
      alert("Failed to duplicate persona");
    }
  };

  const handleArchive = async () => {
    setShowPersonaOptions(false);
    if (!org?.payload || !persona || !window.confirm(`Archive "${persona.name}"? This will hide it from the list but preserve links.`)) {
      return;
    }

    try {
      const personaRef = ref(db, `userData/${org.payload}/personas/${id}`);
      await update(personaRef, { archived: true, updatedAt: { ".sv": "timestamp" } });
      navigate("/personas");
    } catch (error) {
      console.error("Error archiving persona:", error);
      alert("Failed to archive persona");
    }
  };

  // Show loading state if: Not a new persona and persona data hasn't loaded yet
  // For new personas, formData is initialized synchronously, so no loading needed
  if (!isNewPersona && !persona) {
    return (
      <div className="mainContentInner">
        <TitleBar
          title="Loading..."
          actions={[
            {
              icon: "arrow_back",
              name: "Back",
              action: () => navigate("/personas"),
              variants: ["text"]
            }
          ]}
          breadcrumb={[
            { name: "Home", url: "/" },
            { name: "Personas", url: "/personas" }
          ]}
        />
      </div>
    );
  }

  const journeyCount = persona?.linkedJourneys?.length || 0;
  const feedbackCount = persona?.linkedFeedback?.length || 0;

  // Use formData when editing, otherwise use persona data (or empty for new)
  const displayData = isEditing && formData ? formData : (persona || {});

  // Get selected label for Goals
  const goalsLabel = labels.find(l => l.id === (persona?.goalsLabelId || displayData.goalsLabelId));
  const goalsLabelText = goalsLabel ? goalsLabel.value : "Goals";
  const displayDemographics = displayData.demographics || {};
  const displayPersonality = displayData.personality || {
    introvertExtrovert: 50,
    thinkingFeeling: 50,
    sensingIntuition: 50,
    judgingPerceiving: 50
  };
  const displayPhoto = isEditing ? photoPreview : photoURL;
  const displayLabelIds = displayData.labelIds || [];
  const displayLabels = labels.filter(l => displayLabelIds.includes(l.id));
  
  // Handle motivation migration for display
  const displayMotivation = (() => {
    if (isEditing && formData) {
      return formData.motivation || [];
    }
    if (persona?.motivation) {
      if (Array.isArray(persona.motivation)) {
        return persona.motivation;
      } else {
        return Object.entries(persona.motivation).map(([key, value]) => {
          const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim();
          return { label, value };
        });
      }
    }
    return [];
  })();
  
  // Handle preferredChannels migration for display
  const displayPreferredChannels = (() => {
    if (isEditing && formData) {
      return formData.preferredChannels || [];
    }
    if (persona?.preferredChannels) {
      if (Array.isArray(persona.preferredChannels)) {
        return persona.preferredChannels;
      } else {
        return Object.entries(persona.preferredChannels).map(([key, value]) => {
          let label = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim();
          if (label === "Online Social Media") label = "Online & Social Media";
          if (label === "Guerrilla P R") label = "Guerrilla Efforts & PR";
          return { label, value };
        });
      }
    }
    return [];
  })();
  
  const displayBrandIds = displayData.brandIds || [];
  const displayBrands = (() => {
    if (displayBrandIds.length > 0) {
      return brands.filter(b => displayBrandIds.includes(b.id));
    } else if (persona?.brands && Array.isArray(persona.brands) && persona.brands.length > 0) {
      // Old format: try to match by name
      return persona.brands
        .map(brandName => {
          const brand = brands.find(b => b.name === brandName || (typeof brandName === 'object' && brandName.name === b.name));
          return brand || null;
        })
        .filter(b => b !== null);
    }
    return [];
  })();

  return (
    <div className="mainContentInner">
      <TitleBar
        title={isNewPersona ? "New Persona" : (isEditing ? "Editing Persona" : (persona?.name || "Persona"))}
        actions={[
          {
            icon: "arrow_back",
            name: "Back",
            action: () => navigate("/personas"),
            variants: ["text"]
          },
          ...(isEditing ? [
            {
              icon: "close",
              name: "Cancel",
              action: handleCancel,
              variants: ["text"]
            },
            {
              icon: "save",
              name: saving ? "Saving..." : "Save",
              action: handleSave,
              variants: ["primary"],
              disabled: saving
            }
          ] : [
            {
              icon: "more_vert",
              name: "",
              action: showPersonaOptionsHandler,
              variants: ["text"]
            }
          ])
        ]}
        breadcrumb={[
          { name: "Home", url: "/" },
          { name: "Personas", url: "/personas" }
        ]}
      />

      {error && (
        <div className="contentPadding">
          <div className={style.error}>{error}</div>
        </div>
      )}

      <div className="contentPadding">
        <div className={style.threeColumnLayout}>
          {/* Left Column */}
          <div className={style.leftColumn}>
            {/* Photo */}
            {isEditing ? (
              <div className={style.photoUpload}>
                <label>Photo/Avatar</label>
                <div 
                  className={`${style.photoPreviewContainer} ${dragActive ? style.dragActive : ''}`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  {displayPhoto ? (
                    <>
                      <img src={displayPhoto} alt="Preview" className={style.photoPreview} />
                      <Button
                        variants={["text", "small"]}
                        onClick={() => {
                          setPhotoFile(null);
                          setPhotoPreview(null);
                          if (photoInputRef.current) {
                            photoInputRef.current.value = "";
                          }
                        }}
                      >
                        <Icon>delete</Icon> Remove
                      </Button>
                    </>
                  ) : (
                    <div className={style.photoPlaceholder}>
                      <Icon>person</Icon>
                    </div>
                  )}
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileInputChange}
                    style={{ display: 'none' }}
                    id="photo-upload"
                  />
                  <Button
                    variants={["text", "small"]}
                    onClick={handlePhotoButtonClick}
                  >
                    {displayPhoto ? "Change Photo" : "Upload Photo"}
                  </Button>
                </div>
              </div>
            ) : (
              <div className={style.photoContainer}>
                {photoURL ? (
                  <img src={photoURL} alt={persona?.name || "Persona"} className={style.photo} />
                ) : (
                  <div className={style.photoPlaceholder}>
                    <Icon>person</Icon>
                  </div>
                )}
              </div>
            )}

            {/* Quotation */}
            {isEditing ? (
              <div className={style.section}>
                <div className={style.textAreaField}>
                  <label>Quotation</label>
                  <textarea
                    className={style.textarea}
                    value={formData?.quotation || ""}
                    onChange={(e) => handleInputChange("quotation", e.target.value)}
                    placeholder="A quotation that captures this user's personality"
                    rows={3}
                  />
                </div>
              </div>
            ) : (
              displayData.quotation && (
                <div className={style.quotationBox}>
                  <Icon>format_quote</Icon>
                  <p>{displayData.quotation}</p>
                </div>
              )
            )}

            {/* Basic Information */}
            {isEditing ? (
              <div className={style.section}>
                <h3 className={style.sectionTitle}>Demographics</h3>
                <Input
                  type="number"
                  label="Age"
                  value={formData?.demographics?.age || ""}
                  onChange={(e) => handleInputChange("demographics.age", e.target.value)}
                  placeholder="e.g., 32"
                />
                <Input
                  type="text"
                  label="Work"
                  value={formData?.demographics?.work || ""}
                  onChange={(e) => handleInputChange("demographics.work", e.target.value)}
                  placeholder="e.g., Product Manager"
                />
                <Input
                  type="text"
                  label="Family"
                  value={formData?.demographics?.family || ""}
                  onChange={(e) => handleInputChange("demographics.family", e.target.value)}
                  placeholder="e.g., Married, kids, etc."
                />
                <Input
                  type="text"
                  label="Location"
                  value={formData?.demographics?.location || ""}
                  onChange={(e) => handleInputChange("demographics.location", e.target.value)}
                  placeholder="e.g., London, UK"
                />
                <Input
                  type="text"
                  label="Character"
                  value={formData?.demographics?.character || ""}
                  onChange={(e) => handleInputChange("demographics.character", e.target.value)}
                  placeholder="e.g., Archetype"
                />
              </div>
            ) : (
              (displayDemographics.age || displayDemographics.work || displayDemographics.family || displayDemographics.location || displayDemographics.character) && (
                <div className={style.section}>
                  <div className={style.basicInfo}>
                    {displayDemographics.age && (
                      <div className={style.infoItem}>
                        <label>Age</label>
                        <span>{displayDemographics.age}</span>
                      </div>
                    )}
                    {displayDemographics.work && (
                      <div className={style.infoItem}>
                        <label>Work</label>
                        <span>{displayDemographics.work}</span>
                      </div>
                    )}
                    {displayDemographics.family && (
                      <div className={style.infoItem}>
                        <label>Family</label>
                        <span>{displayDemographics.family}</span>
                      </div>
                    )}
                    {displayDemographics.location && (
                      <div className={style.infoItem}>
                        <label>Location</label>
                        <span>{displayDemographics.location}</span>
                      </div>
                    )}
                    {displayDemographics.character && (
                      <div className={style.infoItem}>
                        <label>Character</label>
                        <span>{displayDemographics.character}</span>
                      </div>
                    )}
                  </div>
                </div>
              )
            )}

            {/* Personality Sliders */}
            {isEditing ? (
              <div className={style.section}>
                <h3 className={style.sectionTitle}>Personality</h3>
                <div className={style.sliderField}>
                  <label>Introvert ↔ Extrovert</label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={formData?.personality?.introvertExtrovert || 50}
                    onChange={(e) => handleInputChange("personality.introvertExtrovert", parseInt(e.target.value))}
                    className={style.rangeInput}
                  />
                  <div className={style.sliderValue}>{formData?.personality?.introvertExtrovert || 50}%</div>
                </div>
                <div className={style.sliderField}>
                  <label>Thinking ↔ Feeling</label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={formData?.personality?.thinkingFeeling || 50}
                    onChange={(e) => handleInputChange("personality.thinkingFeeling", parseInt(e.target.value))}
                    className={style.rangeInput}
                  />
                  <div className={style.sliderValue}>{formData?.personality?.thinkingFeeling || 50}%</div>
                </div>
                <div className={style.sliderField}>
                  <label>Sensing ↔ Intuition</label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={formData?.personality?.sensingIntuition || 50}
                    onChange={(e) => handleInputChange("personality.sensingIntuition", parseInt(e.target.value))}
                    className={style.rangeInput}
                  />
                  <div className={style.sliderValue}>{formData?.personality?.sensingIntuition || 50}%</div>
                </div>
                <div className={style.sliderField}>
                  <label>Judging ↔ Perceiving</label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={formData?.personality?.judgingPerceiving || 50}
                    onChange={(e) => handleInputChange("personality.judgingPerceiving", parseInt(e.target.value))}
                    className={style.rangeInput}
                  />
                  <div className={style.sliderValue}>{formData?.personality?.judgingPerceiving || 50}%</div>
                </div>
              </div>
            ) : (
              displayPersonality && (
                <div className={style.section}>
                  <h3 className={style.sectionTitle}>Personality</h3>
                  <PersonalitySlider
                    label="Introvert / Extrovert"
                    leftLabel="Introvert"
                    rightLabel="Extrovert"
                    value={displayPersonality.introvertExtrovert}
                  />
                  <PersonalitySlider
                    label="Thinking / Feeling"
                    leftLabel="Thinking"
                    rightLabel="Feeling"
                    value={displayPersonality.thinkingFeeling}
                  />
                  <PersonalitySlider
                    label="Sensing / Intuition"
                    leftLabel="Sensing"
                    rightLabel="Intuition"
                    value={displayPersonality.sensingIntuition}
                  />
                  <PersonalitySlider
                    label="Judging / Perceiving"
                    leftLabel="Judging"
                    rightLabel="Perceiving"
                    value={displayPersonality.judgingPerceiving}
                  />
                </div>
              )
            )}
          </div>

          {/* Middle Column */}
          <div className={style.middleColumn}>
            {/* Name and Tagline */}
            {isEditing ? (
              <div className={style.section}>
                <Input
                  type="text"
                  label="Persona Name *"
                  value={formData?.name || ""}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  placeholder="e.g., Sarah the Busy Professional"
                />
                <Input
                  type="text"
                  label="Tagline *"
                  value={formData?.tagline || ""}
                  onChange={(e) => handleInputChange("tagline", e.target.value)}
                  placeholder="A short description of this persona"
                />
                <div className={style.fieldGroup}>
                  <label>Labels</label>
                  <Select
                    type="dropdown"
                    options={labels.map(l => ({ 
                      value: l.value,
                      selected: (formData?.labelIds || []).includes(l.id)
                    }))}
                    selectedOptions={labels
                      .filter(l => (formData?.labelIds || []).includes(l.id))
                      .map(l => l.value)}
                    onChange={(e, index) => {
                      const selectedLabelId = labels[index]?.id;
                      if (selectedLabelId) {
                        const currentIds = formData?.labelIds || [];
                        if (currentIds.includes(selectedLabelId)) {
                          setFormData(prev => ({ 
                            ...prev, 
                            labelIds: currentIds.filter(id => id !== selectedLabelId)
                          }));
                        } else {
                          setFormData(prev => ({ 
                            ...prev, 
                            labelIds: [...currentIds, selectedLabelId]
                          }));
                        }
                      }
                    }}
                    multiselect={true}
                  />
                </div>
              </div>
            ) : (
              <div className={style.section}>
                <h1 className={style.personaName}>{displayData.name}</h1>
                {displayData.tagline && (
                  <p className={style.tagline}>{displayData.tagline}</p>
                )}
                {displayLabels.length > 0 && (
                  <div className={style.labels}>
                    {displayLabels.map((label) => (
                      <Label
                        key={label.id}
                        data={label}
                        variants={["selected"]}
                      >
                        {label.value}
                      </Label>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Goals */}
            {isEditing ? (
              <div className={style.section}>
                <div className={style.arrayField}>
                  <label>Goals</label>
                  {(formData?.goals || []).map((goal, index) => (
                    <div key={index} className={style.arrayItem}>
                      <Input
                        type="text"
                        value={goal}
                        onChange={(e) => {
                          const newGoals = [...(formData?.goals || [])];
                          newGoals[index] = e.target.value;
                          setFormData(prev => ({ ...prev, goals: newGoals }));
                        }}
                        placeholder="A task that needs to be completed, a life goal, or an experience to be felt"
                      />
                      <Button
                        variants={["text", "small"]}
                        onClick={() => {
                          const newGoals = (formData?.goals || []).filter((_, i) => i !== index);
                          setFormData(prev => ({ ...prev, goals: newGoals }));
                        }}
                      >
                        <Icon>delete</Icon>
                      </Button>
                    </div>
                  ))}
                  <Button
                    variants={["text", "small"]}
                    onClick={() => setFormData(prev => ({ ...prev, goals: [...(prev?.goals || []), ""] }))}
                  >
                    <Icon>add</Icon> Add Goal
                  </Button>
                </div>
              </div>
            ) : (
              displayData.goals?.length > 0 && (
                <div className={style.section}>
                  <h3 className={style.sectionTitle}>{goalsLabelText}</h3>
                  <ul className={style.goalsList}>
                    {displayData.goals.map((goal, index) => (
                      <li key={index}>{goal}</li>
                    ))}
                  </ul>
                </div>
              )
            )}

            {/* Frustrations */}
            {isEditing ? (
              <div className={style.section}>
                <div className={style.arrayField}>
                  <label>Frustrations</label>
                  {(formData?.frustrations || []).map((frustration, index) => (
                    <div key={index} className={style.arrayItem}>
                      <Input
                        type="text"
                        value={frustration}
                        onChange={(e) => {
                          const newFrustrations = [...(formData?.frustrations || [])];
                          newFrustrations[index] = e.target.value;
                          setFormData(prev => ({ ...prev, frustrations: newFrustrations }));
                        }}
                        placeholder="The challenges this user would like to avoid, obstacles, or problems with available solutions"
                      />
                      <Button
                        variants={["text", "small"]}
                        onClick={() => {
                          const newFrustrations = (formData?.frustrations || []).filter((_, i) => i !== index);
                          setFormData(prev => ({ ...prev, frustrations: newFrustrations }));
                        }}
                      >
                        <Icon>delete</Icon>
                      </Button>
                    </div>
                  ))}
                  <Button
                    variants={["text", "small"]}
                    onClick={() => setFormData(prev => ({ ...prev, frustrations: [...(prev?.frustrations || []), ""] }))}
                  >
                    <Icon>add</Icon> Add Frustration
                  </Button>
                </div>
              </div>
            ) : (
              displayData.frustrations?.length > 0 && (
                <div className={style.section}>
                  <h3 className={style.sectionTitle}>Frustrations</h3>
                  <ul className={style.frustrationsList}>
                    {displayData.frustrations.map((frustration, index) => (
                      <li key={index}>{frustration}</li>
                    ))}
                  </ul>
                </div>
              )
            )}

            {/* Bio */}
            {isEditing ? (
              <div className={style.section}>
                <div className={style.textAreaField}>
                  <label>Bio</label>
                  <textarea
                    className={style.textarea}
                    value={formData?.about || ""}
                    onChange={(e) => handleInputChange("about", e.target.value)}
                    placeholder="The bio should be a short paragraph to describe the user journey. It should include some of their history leading up to a current use case..."
                    rows={8}
                  />
                </div>
              </div>
            ) : (
              displayData.about && (
                <div className={style.section}>
                  <h3 className={style.sectionTitle}>Bio</h3>
                  <div
                    className={style.bio}
                    dangerouslySetInnerHTML={{ __html: displayData.about }}
                  />
                </div>
              )
            )}

            {/* Linked Content - Only show when not creating new */}
            {!isNewPersona && (journeyCount > 0 || feedbackCount > 0 || persona.tags?.length > 0) && (
              <div className={style.section}>
                <h3 className={style.sectionTitle}>Links & Associations</h3>
                {journeyCount > 0 && (
                  <div className={style.linkedSection}>
                    <h4>Linked Journeys</h4>
                    <div className={style.linkedItems}>
                      {persona.linkedJourneys.map((journey, index) => (
                        <Button
                          key={index}
                          variants={["text"]}
                          onClick={() => navigate(`/journeys/${journey.journeyId}`)}
                        >
                          <Icon>route</Icon> {journey.journeyName}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
                {persona.tags?.length > 0 && (
                  <div className={style.tags}>
                    {persona.tags.map((tag, index) => (
                      <span key={index} className={style.tag}>{tag}</span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Column */}
          <div className={style.rightColumn}>
            {/* Motivation */}
            {isEditing ? (
              <div className={style.section}>
                <h3 className={style.sectionTitle}>Motivation</h3>
                {(formData?.motivation || []).map((item, index) => (
                  <div key={index} className={style.configurableField}>
                    <div className={style.configurableFieldHeader}>
                      <Input
                        type="text"
                        value={item.label}
                        onChange={(e) => {
                          const newMotivation = [...(formData?.motivation || [])];
                          newMotivation[index] = { ...newMotivation[index], label: e.target.value };
                          setFormData(prev => ({ ...prev, motivation: newMotivation }));
                        }}
                        placeholder="Motivation type (e.g., Incentive, Fear, Growth)"
                      />
                      <Button
                        variants={["text", "small"]}
                        onClick={() => {
                          const newMotivation = (formData?.motivation || []).filter((_, i) => i !== index);
                          setFormData(prev => ({ ...prev, motivation: newMotivation }));
                        }}
                      >
                        <Icon>delete</Icon>
                      </Button>
                    </div>
                    <div className={style.sliderField}>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={item.value}
                        onChange={(e) => {
                          const newMotivation = [...(formData?.motivation || [])];
                          newMotivation[index] = { ...newMotivation[index], value: parseInt(e.target.value) };
                          setFormData(prev => ({ ...prev, motivation: newMotivation }));
                        }}
                        className={style.rangeInput}
                      />
                      <div className={style.sliderValue}>{item.value}%</div>
                    </div>
                  </div>
                ))}
                <Button
                  variants={["text", "small"]}
                  onClick={() => setFormData(prev => ({ ...prev, motivation: [...(prev?.motivation || []), { label: "", value: 0 }] }))}
                >
                  <Icon>add</Icon> Add Motivation
                </Button>
              </div>
            ) : (
              displayMotivation.length > 0 && (
                <div className={style.section}>
                  <h3 className={style.sectionTitle}>Motivation</h3>
                  {displayMotivation.map((item, index) => (
                    <MotivationBar key={index} label={item.label} value={item.value} />
                  ))}
                </div>
              )
            )}

            {/* Brands & Influencers */}
            {isEditing ? (
              <div className={style.section}>
                <h3 className={style.sectionTitle}>Brands & Influencers</h3>
                <div className={style.fieldGroup}>
                  <Select
                    type="dropdown"
                    options={brands.map(b => ({ 
                      value: b.name,
                      selected: (formData?.brandIds || []).includes(b.id)
                    }))}
                    selectedOptions={brands
                      .filter(b => (formData?.brandIds || []).includes(b.id))
                      .map(b => b.name)}
                    onChange={(e, index) => {
                      const selectedBrandId = brands[index]?.id;
                      if (selectedBrandId) {
                        const currentIds = formData?.brandIds || [];
                        if (currentIds.includes(selectedBrandId)) {
                          setFormData(prev => ({ 
                            ...prev, 
                            brandIds: currentIds.filter(id => id !== selectedBrandId)
                          }));
                        } else {
                          setFormData(prev => ({ 
                            ...prev, 
                            brandIds: [...currentIds, selectedBrandId]
                          }));
                        }
                      }
                    }}
                    multiselect={true}
                  />
                </div>
              </div>
            ) : (
              displayBrands.length > 0 && (
                <div className={style.section}>
                  <h3 className={style.sectionTitle}>Brands & Influencers</h3>
                  <div className={style.brandsList}>
                    {displayBrands.map((brand) => (
                      <div key={brand.id} className={style.brandItem}>
                        <Icon>folder</Icon>
                        <span>{brand.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )
            )}

            {/* Preferred Channels */}
            {isEditing ? (
              <div className={style.section}>
                <h3 className={style.sectionTitle}>Preferred Channels</h3>
                {(formData?.preferredChannels || []).map((item, index) => (
                  <div key={index} className={style.configurableField}>
                    <div className={style.configurableFieldHeader}>
                      <Input
                        type="text"
                        value={item.label}
                        onChange={(e) => {
                          const newChannels = [...(formData?.preferredChannels || [])];
                          newChannels[index] = { ...newChannels[index], label: e.target.value };
                          setFormData(prev => ({ ...prev, preferredChannels: newChannels }));
                        }}
                        placeholder="Channel name (e.g., Traditional Ads, Online & Social Media)"
                      />
                      <Button
                        variants={["text", "small"]}
                        onClick={() => {
                          const newChannels = (formData?.preferredChannels || []).filter((_, i) => i !== index);
                          setFormData(prev => ({ ...prev, preferredChannels: newChannels }));
                        }}
                      >
                        <Icon>delete</Icon>
                      </Button>
                    </div>
                    <div className={style.sliderField}>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={item.value}
                        onChange={(e) => {
                          const newChannels = [...(formData?.preferredChannels || [])];
                          newChannels[index] = { ...newChannels[index], value: parseInt(e.target.value) };
                          setFormData(prev => ({ ...prev, preferredChannels: newChannels }));
                        }}
                        className={style.rangeInput}
                      />
                      <div className={style.sliderValue}>{item.value}%</div>
                    </div>
                  </div>
                ))}
                <Button
                  variants={["text", "small"]}
                  onClick={() => setFormData(prev => ({ ...prev, preferredChannels: [...(prev?.preferredChannels || []), { label: "", value: 0 }] }))}
                >
                  <Icon>add</Icon> Add Channel
                </Button>
              </div>
            ) : (
              displayPreferredChannels.length > 0 && (
                <div className={style.section}>
                  <h3 className={style.sectionTitle}>Preferred Channels</h3>
                  {displayPreferredChannels.map((item, index) => (
                    <ChannelBar key={index} label={item.label} value={item.value} />
                  ))}
                </div>
              )
            )}
          </div>
        </div>
      </div>

      {!isNewPersona && (
        <Dropdown
          show={showPersonaOptions}
          onClose={closePersonaOptionsHandler}
          style={personaOptionsStyle}
        >
          <ul className={style.personaOptions}>
            {canEdit && (
              <>
                <li>
                  <Button
                    preIcon="edit"
                    onClick={handleEdit}
                    variants={["text"]}
                  >
                    Edit
                  </Button>
                </li>
                <li>
                  <Button
                    preIcon="content_copy"
                    onClick={handleDuplicate}
                    variants={["text"]}
                  >
                    Duplicate
                  </Button>
                </li>
                <li>
                  <Button
                    preIcon="archive"
                    onClick={handleArchive}
                    variants={["text"]}
                  >
                    Archive
                  </Button>
                </li>
              </>
            )}
          </ul>
        </Dropdown>
      )}

    </div>
  );
};

export default PersonaDetail;
